import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';   // Block 233
import { showErrorToast } from '../../utils/toast';        // Block 233
import { PageLayout, PageHeader, Card, StatTile, Button, Field, Input, DateInput, EmptyState } from '../../ui';
import { fmtMoney as fmt } from './money';
import './financials.css';

const today = new Date().toISOString().slice(0, 10);


function fmtDate(str) {
  if (!str) return '';
  return new Date(str + 'T12:00:00').toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

const EMPTY_FORM = { date: today, amount: '', description: '', counterparty: '' };

// ─── Transaction panel (reused for income and expense) ────────────────────────

function TransactionPanel({
  title, color, counterpartyLabel, buttonLabel,
  form, setForm, entries, saving, onSubmit, onDelete,
}) {
  return (
    <Card color={color} label={title} lift={false}>
      {/* Form */}
      <form onSubmit={onSubmit} className="fin-basic-form">
        <div className="fin-basic-form__row">
          <Field label="Date">
            <DateInput
              value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              required
            />
          </Field>
          <Field label="Amount (£)">
            <Input
              type="number"
              min="0.01"
              step="0.01"
              inputMode="decimal"
              placeholder="0.00"
              value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              required
            />
          </Field>
        </div>

        <Field label="Description">
          <Input
            placeholder="What was this for?"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            required
          />
        </Field>

        <Field label={counterpartyLabel}>
          <Input
            placeholder="Name or company"
            value={form.counterparty}
            onChange={e => setForm(f => ({ ...f, counterparty: e.target.value }))}
          />
        </Field>

        <Button type="submit" variant="confirm" disabled={saving} style={{ alignSelf: 'flex-start' }}>
          {saving ? 'Adding…' : buttonLabel}
        </Button>
      </form>

      {/* Entry list */}
      {entries.length === 0 ? (
        <EmptyState>No entries yet</EmptyState>
      ) : (
        <ul className="fin-basic-entries">
          {entries.map(t => (
            <li key={t.id} className="fin-basic-entry">
              <span className="fin-basic-entry__date">{fmtDate(t.date)}</span>
              <span className="fin-basic-entry__desc">
                {t.description}
                {t.counterparty && <small>{t.counterparty}</small>}
              </span>
              <span className="fin-basic-entry__amt">{fmt(t.amount)}</span>
              <button
                type="button"
                onClick={() => onDelete(t.id)}
                title="Delete"
                aria-label={`Delete ${t.description}`}
                className="fin-basic-entry__del ui-hit"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function BasicFinancialSummary({ setFilter, onUpgrade, readOnly = false, invoicesIncluded = false }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incomeForm, setIncomeForm] = useState({ ...EMPTY_FORM });
  const [expenseForm, setExpenseForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState({ income: false, expense: false });

  useEffect(() => {
    let cancelled = false;
    api('/api/basic-transactions')
      .then(r => r.ok ? r.json() : { transactions: [] })
      .then(data => { if (!cancelled) setTransactions(data.transactions || []); })
      .catch(() => { })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // In readOnly mode, scope totals to the current calendar month only
  const thisMonth = new Date().toISOString().slice(0, 7); // "YYYY-MM"
  const visibleTx = readOnly
    ? transactions.filter(t => (t.date || '').slice(0, 7) === thisMonth)
    : transactions;
  const income = visibleTx.filter(t => t.type === 'income');
  const expenses = visibleTx.filter(t => t.type === 'expense');
  const totalIn = income.reduce((s, t) => s + t.amount, 0);
  const totalOut = expenses.reduce((s, t) => s + t.amount, 0);
  const balance = totalIn - totalOut;

  async function addTransaction(type, form, setForm, key) {
    if (!form.amount || !form.description || !form.date) return;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      const res = await api('/api/basic-transactions', {
        method: 'POST',
        body: JSON.stringify({
          type,
          amount: parseFloat(form.amount),
          description: form.description.trim(),
          counterparty: form.counterparty.trim(),
          date: form.date,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setTransactions(prev =>
          [data.transaction, ...prev].sort((a, b) =>
            b.date.localeCompare(a.date) || b.createdAt?.localeCompare(a.createdAt || '') || 0
          )
        );
        setForm({ ...EMPTY_FORM });
      }
    } catch (err) { showErrorToast(messageFromError(err, "Could not add transaction.")); }
    setSaving(s => ({ ...s, [key]: false }));
  }

  async function deleteTransaction(id) {
    try {
      const res = await api(`/api/basic-transactions/${id}`, { method: 'DELETE' });
      if (res.ok) setTransactions(prev => prev.filter(t => t.id !== id));
    } catch (err) { showErrorToast(messageFromError(err, "Could not delete transaction.")); }
  }

  const tiles = [
    { label: 'Money in', value: totalIn, color: 'pastel-mint' },
    { label: 'Money out', value: totalOut, color: 'pastel-pink' },
    { label: 'Balance', value: balance, color: balance >= 0 ? 'pastel-mint' : 'pastel-coral' },
  ];

  const upgrade = () => {
    if (onUpgrade) {
      onUpgrade('pro');
      return;
    }
    if (setFilter) setFilter('Settings');
  };

  return (
    <PageLayout className="ui-page fin-page">
      <PageHeader
        eyebrow={{ label: 'Business workspace', color: 'group-overview' }}
        title="Financial overview"
        subline={readOnly
          ? `This month's summary, ${new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`
          : 'Track your income and expenses'}
      />

      {/* Summary tiles */}
      <div className="fin-ytd">
        {tiles.map(({ label, value, color }) => (
          <StatTile key={label} card color={color} label={label} value={loading ? '—' : fmt(value)} />
        ))}
      </div>

      {/* Transaction logger — hidden in readOnly (Basic plan) mode */}
      {!readOnly && (
        <div className="fin-basic-grid">
          <TransactionPanel
            title="Money in"
            color="pastel-mint"
            counterpartyLabel="From (who paid you)"
            buttonLabel="+ Add income"
            form={incomeForm}
            setForm={setIncomeForm}
            entries={income}
            saving={saving.income}
            onSubmit={e => { e.preventDefault(); addTransaction('income', incomeForm, setIncomeForm, 'income'); }}
            onDelete={deleteTransaction}
          />
          <TransactionPanel
            title="Money out"
            color="pastel-pink"
            counterpartyLabel="To (who you paid)"
            buttonLabel="+ Add expense"
            form={expenseForm}
            setForm={setExpenseForm}
            entries={expenses}
            saving={saving.expense}
            onSubmit={e => { e.preventDefault(); addTransaction('expense', expenseForm, setExpenseForm, 'expense'); }}
            onDelete={deleteTransaction}
          />
        </div>
      )}

      {/* Invoice card. UI-24 item 19: invoicing is in Basics (10 sent a month), so a Basics account is
          sent to its Invoices, not told to upgrade to make one. Below Basics it is the upsell. */}
      {invoicesIncluded ? (
      <Card color="pastel-periwinkle" label="Invoices" lift={false}>
        <div className="fin-basic-upsell">
          <span className="fin-basic-upsell__icon" aria-hidden="true">🧾</span>
          <div className="fin-basic-upsell__body">
            <h3 className="ui-card__title">Create and send professional invoices</h3>
            <p>Your plan sends up to 10 a month. Upgrade to Pro for unlimited invoices.</p>
            <Button variant="create" onClick={() => setFilter && setFilter('Invoices')}>Open Invoices</Button>
          </div>
        </div>
      </Card>
      ) : (
      <Card color="pastel-periwinkle" label="Invoices" lift={false}>
        <div className="fin-basic-upsell">
          <span className="fin-basic-upsell__icon" aria-hidden="true">🧾</span>
          <div className="fin-basic-upsell__body">
            <h3 className="ui-card__title">Want to create and send professional invoices?</h3>
            <p>
              Upgrade to Pro for full invoicing, expense tracking with receipt uploads, financial charts,
              VAT summaries, and cash flow reporting.
            </p>
            <Button variant="create" onClick={upgrade}>Create invoice: upgrade to Pro</Button>
          </div>
        </div>
      </Card>
      )}

      {/* Full-suite upgrade prompt (moved below transaction logger) */}
      <Card color="pastel-neutral" label="Pro" lift={false}>
        <div className="fin-basic-upsell">
          <span className="fin-basic-upsell__icon" aria-hidden="true">📊</span>
          <div className="fin-basic-upsell__body">
            <h3 className="ui-card__title">Upgrade to Pro for the full financial suite</h3>
            <p>
              Pro includes invoice creation &amp; sending, expense tracking, financial charts, VAT summaries,
              cash flow timeline, and full reporting.
            </p>
            <Button variant="secondary" onClick={upgrade}>Upgrade to Pro</Button>
          </div>
        </div>
      </Card>
    </PageLayout>
  );
}
